/**
 * Zone and splitter pickers that can make one.
 *
 * The operator's complaint, and it is the whole reason this file exists: a
 * splitter that has not been recorded yet stops the form you are standing in.
 * You are placing an ONU, the FAT it hangs off is new, and the only way
 * forward is to leave the dialog, go to Topology, make the splitter, come
 * back, and start the placement again. The work is two fields long and the
 * detour costs everything you had typed.
 *
 * So the last row of the list is an action: make it here, and the picker
 * selects what you made. `SearchSelect` renders that row pinned below its
 * options — never as one of them, because a picker in this platform names
 * things that exist and an action row is not a thing that exists.
 *
 * ## What these own, so the call sites do not
 *
 * Six places pick a zone or a splitter — the ONU placement dialog, the ONU
 * detail page, the cascade wizard, the ODF page, the topology page and the
 * bulk zone action. Each one of them wiring its own dialog, its own capability
 * check and its own cache invalidation is six chances to get one of the three
 * wrong. These components carry all three, so a call site is one element.
 *
 * ## The capability, and why the row disappears rather than refuses
 *
 * Creating either needs `topology.manage`. Without it the create row is not
 * rendered at all — `onCreate` is simply not passed. An action offered and
 * then refused is worse than an action not offered: the operator has already
 * decided to do the thing by the time the platform says no. Same rule as the
 * nav, which removes a tab the person cannot use rather than disabling it.
 *
 * ## Deliberately minimal creates
 *
 * A zone made from here has a name. A splitter has a name, a kind, a capacity
 * and the zone it sits in. Everything else these objects can carry — a
 * boundary, a parent, coordinates, a PON port, notes — is left to the Topology
 * page, because this is a detour from other work and a form that asks for a
 * polygon is not a detour any more. What is made here is real and complete
 * enough to place equipment against; it is not a substitute for the page that
 * models the network.
 *
 * ## The create runs in the tasks tray
 *
 * Pressing Create closes the little modal at once and the POST runs as a
 * background task, like every modal write on this platform. The picker's own
 * dialog stays open underneath with everything typed in it, so nothing is
 * lost; when the create lands, the task's `done` selects the new object in
 * the picker — after the topology query has been refetched, so the trigger
 * can show its name rather than a placeholder for an id it does not know yet.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SPLITTER_CAPACITIES,
  SPLITTER_KIND_LABELS,
  splitterKindSchema,
  type Splitter,
  type SplitterKind,
  type Zone,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { Button, Field, SearchSelect } from './ui';

/** The one key both objects live under; see the Topology page's own query. */
const TOPOLOGY = ['topology'] as const;

/**
 * The modal both creates share.
 *
 * Rendered above whatever opened it — these pickers are themselves usually
 * inside a dialog, so this sits at a higher z-index than the one it interrupts
 * and dims it rather than replacing it. The operator can see the form they
 * were filling in behind the two fields they came here for.
 */
function QuickCreate({
  title,
  hint,
  disabled,
  onCancel,
  onSubmit,
  children,
}: {
  title: string;
  hint: string;
  disabled: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}) {
  /*
    A DIV, and every button inside it `type="button"`. Not a form, which is what
    this was first and which broke immediately in the ODF dialog.

    These pickers are used INSIDE other dialogs, and those dialogs are forms —
    the ODF one is `<form onSubmit={...}>`. A form nested inside a form is
    invalid HTML: the browser drops the inner one entirely, so a `type="submit"`
    button here submitted the OUTER form instead. Pressing Create in "New zone"
    created the half-filled ODF behind it and closed both dialogs, and no zone
    was made at all. Enter is handled explicitly below for the same reason —
    there is no form to press it against.
  */
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-sm rounded-xl border border-line bg-surface shadow-2xl"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
            return;
          }
          if (e.key === 'Enter' && !disabled) {
            // Stopped as well as prevented: the dialog underneath is listening
            // for its own Enter, and this one is not for it.
            e.preventDefault();
            e.stopPropagation();
            onSubmit();
          }
        }}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-xs text-muted">{hint}</p>
        </header>
        <div className="space-y-4 p-5">{children}</div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={disabled} onClick={onSubmit}>
            Create
          </Button>
        </footer>
      </div>
    </div>
  );
}

export function ZoneSelect({
  value,
  zones,
  onChange,
  disabled = false,
  emptyLabel,
  placeholder,
  className,
}: {
  value: string;
  zones: Array<Pick<Zone, 'id' | 'name'>>;
  onChange: (id: string) => void;
  disabled?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  className?: string;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const [draft, setDraft] = useState<string | null>(null);

  /*
    The trigger says what the empty row says. Without this it read "Select…"
    while the row beneath it read "No zone" — two names for one state, and the
    native select this replaced showed the second.
  */
  const shownPlaceholder = placeholder ?? emptyLabel;

  const create = (name: string) => {
    setDraft(null);
    tasks.start<Zone>({
      label: `Create zone ${name}`,
      run: async () => {
        const zone = await api.post<Zone>('/zones', { name });
        /*
          Invalidate BEFORE selecting, inside the task rather than through its
          `invalidate` list (which runs after `done`). The caller's own `zones`
          prop comes from the topology query, so selecting an id the list has
          not been refetched with would leave the trigger showing the
          placeholder for a zone that exists — the select renders the option's
          label, and it looks up that label in a list the new zone is not in yet.
        */
        await queryClient.invalidateQueries({ queryKey: TOPOLOGY });
        return zone;
      },
      done: (zone) => {
        onChange(zone.id);
        return `Zone ${zone.name} created and selected.`;
      },
    });
  };

  return (
    <>
      <SearchSelect
        value={value}
        disabled={disabled}
        className={className}
        {...(shownPlaceholder === undefined ? {} : { placeholder: shownPlaceholder })}
        {...(emptyLabel === undefined ? {} : { emptyLabel })}
        options={zones.map((z) => ({ value: z.id, label: z.name }))}
        onChange={onChange}
        {...(can('topology.manage') && !disabled
          ? {
              createLabel: 'New zone…',
              onCreate: (typed: string) => setDraft(typed),
            }
          : {})}
      />
      {draft !== null ? (
        <QuickCreate
          title="New zone"
          hint="A service area. Everything else about it is set on the Topology page."
          disabled={draft.trim().length === 0}
          onCancel={() => setDraft(null)}
          onSubmit={() => create(draft.trim())}
        >
          <Field label="Name">
            <input
              className="input"
              autoFocus
              value={draft}
              maxLength={120}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Kikuyu North"
            />
          </Field>
        </QuickCreate>
      ) : null}
    </>
  );
}

export function SplitterSelect({
  value,
  splitters,
  onChange,
  /** Pre-filled on a splitter made from here, so it lands where you are looking. */
  zoneId = null,
  disabled = false,
  emptyLabel,
  placeholder,
  className,
  optionHint,
}: {
  value: string;
  splitters: Array<Pick<Splitter, 'id' | 'name'>>;
  onChange: (id: string) => void;
  zoneId?: string | null;
  disabled?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  className?: string;
  /** The second line per row — "1:8 · 5/8 used" and the like, from the caller. */
  optionHint?: (splitter: Pick<Splitter, 'id' | 'name'>) => string | null;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const [draft, setDraft] = useState<string | null>(null);
  /*
    `fat` by default because that is what a picker-driven create almost always
    is: you are placing a subscriber and the box at the end of the drop has not
    been recorded. An ODF or an enclosure is a planned piece of the distribution
    network and belongs on the Topology page, where its parent and its position
    can be set — but the operator can still say so here rather than being told
    what they meant.
  */
  const [kind, setKind] = useState<SplitterKind>('fat');
  const [capacity, setCapacity] = useState(8);

  /*
    The trigger says what the empty row says. Without this it read "Select…"
    while the row beneath it read "No zone" — two names for one state, and the
    native select this replaced showed the second.
  */
  const shownPlaceholder = placeholder ?? emptyLabel;

  const create = (name: string) => {
    // Snapshotted: the modal is gone by the time the write settles.
    const body = {
      name,
      kind,
      capacity,
      // Null rather than omitted: a splitter made from a picker that has no
      // zone chosen is genuinely unzoned, and the API distinguishes the two.
      zoneId: zoneId ?? null,
    };
    setDraft(null);
    tasks.start<Splitter>({
      label: `Create ${SPLITTER_KIND_LABELS[kind]} ${name}`,
      run: async () => {
        const splitter = await api.post<Splitter>('/splitters', body);
        // Refetched before it is selected — same reason as the zone above.
        await queryClient.invalidateQueries({ queryKey: TOPOLOGY });
        return splitter;
      },
      done: (splitter) => {
        onChange(splitter.id);
        return `${splitter.name} created and selected.`;
      },
    });
  };

  return (
    <>
      <SearchSelect
        value={value}
        disabled={disabled}
        className={className}
        {...(shownPlaceholder === undefined ? {} : { placeholder: shownPlaceholder })}
        {...(emptyLabel === undefined ? {} : { emptyLabel })}
        options={splitters.map((s) => ({
          value: s.id,
          label: s.name,
          hint: optionHint?.(s) ?? null,
        }))}
        onChange={onChange}
        {...(can('topology.manage') && !disabled
          ? {
              createLabel: 'New splitter…',
              onCreate: (typed: string) => setDraft(typed),
            }
          : {})}
      />
      {draft !== null ? (
        <QuickCreate
          title="New splitter"
          hint={
            zoneId
              ? 'It lands in the zone selected above. Ports, loss and position are set on the Topology page.'
              : 'Not in a zone — pick one above first if it belongs to one. Ports, loss and position are set on the Topology page.'
          }
          disabled={draft.trim().length === 0}
          onCancel={() => setDraft(null)}
          onSubmit={() => create(draft.trim())}
        >
          <Field label="Name">
            <input
              className="input"
              autoFocus
              value={draft}
              maxLength={120}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="FAT-12"
            />
          </Field>
          <Field label="Kind" hint="Where it sits on the light path: ODF, then enclosure, then FAT">
            {/*
              The platform's own three kinds and its own labels, not a pair
              invented here. They are stages on one light path — the model's
              words are `OLT port -> ODF -> enclosure -> FAT -> ONU` — and a
              picker that offered a fourth name, or omitted one of the three,
              would be describing a different network from the one the cascade
              builder and the create route both enforce.
            */}
            <select
              className="input"
              value={kind}
              onChange={(e) => setKind(e.target.value as SplitterKind)}
            >
              {splitterKindSchema.options.map((k) => (
                <option key={k} value={k}>
                  {SPLITTER_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Outputs" hint="How many ways it splits. Changeable later.">
            <select
              className="input"
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
            >
              {SPLITTER_CAPACITIES.map((n) => (
                <option key={n} value={n}>
                  1:{n}
                </option>
              ))}
            </select>
          </Field>
        </QuickCreate>
      ) : null}
    </>
  );
}
